import { useEffect, useState } from "react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { Check, X } from "lucide-react"
import clsx from "clsx"
import { api, ApiError, type PermissionRequest, type Tag } from "@/lib/types"
import { GlarchiveSubNav } from "@/components/GlarchiveSubNav"
import { formatRelative } from "@/lib/format"
import { TagChip } from "@lilbillbiscuit/account-ui"

type StatusFilter = "pending" | "approved" | "denied" | "all"

export function AdminRequests() {
  const [status, setStatus] = useState<StatusFilter>("pending")
  const requests = useQuery<{ items: PermissionRequest[]; total: number }, ApiError>({
    queryKey: ["adminRequests", status],
    queryFn: () => api("/api/admin/requests", { query: { status, limit: 100 } }),
  })
  const tags = useQuery<{ tags: Tag[] }, ApiError>({
    queryKey: ["adminTagsAll"],
    queryFn: () => api("/api/admin/tags"),
  })

  return (
    <div className="space-y-6">
      <GlarchiveSubNav />
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-heading text-2xl font-semibold">Access requests</h1>
        <div role="radiogroup" aria-label="Status filter" className="flex rounded-lg border border-th-glass/[0.12] bg-th-glass/[0.04] p-0.5 text-sm">
          {(["pending", "approved", "denied", "all"] as StatusFilter[]).map((s) => (
            <button
              key={s}
              role="radio"
              aria-checked={status === s}
              onClick={() => setStatus(s)}
              className={clsx(
                "rounded-md px-2.5 py-1 text-xs transition-colors",
                status === s ? "bg-th-glass/[0.1] text-th-fg" : "text-th-fg/50 hover:text-th-fg"
              )}
            >{s}</button>
          ))}
        </div>
      </div>

      {requests.error && (
        <div className="rounded-lg border border-red-500/40 bg-red-500/10 p-3 text-sm text-red-400">
          {requests.error.serverMessage ?? "Failed to load requests."}
        </div>
      )}

      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        {requests.data?.items.map((r) => (
          <RequestCard
            key={r.id}
            request={r}
            allTags={tags.data?.tags ?? []}
            tagsLoaded={!tags.isLoading}
          />
        ))}
        {!requests.isLoading && (requests.data?.items.length ?? 0) === 0 && (
          <div className="md:col-span-2 py-12 text-center text-sm text-th-fg/40">No requests in this state.</div>
        )}
      </div>
    </div>
  )
}

function RequestCard({
  request, allTags, tagsLoaded,
}: { request: PermissionRequest; allTags: Tag[]; tagsLoaded: boolean }) {
  const qc = useQueryClient()
  const requestedTerms: string[] = (() => {
    try { return request.requested_tag_terms ? JSON.parse(request.requested_tag_terms) : [] } catch { return [] }
  })()
  const [selected, setSelected] = useState<string[]>([])

  // Pre-select once tags actually arrive. Only the terms that match a real tag
  // slug make sense to pre-check; the rest stay as informational chips above.
  // Intentionally re-runs only when tags finish loading or their count changes
  // (we don't want to clobber the admin's edits on every render).
  useEffect(() => {
    if (!tagsLoaded || selected.length > 0) return
    setSelected(requestedTerms.filter((t) => allTags.some((tag) => tag.slug === t)))
  }, [tagsLoaded, allTags.length]) // eslint-disable-line react-hooks/exhaustive-deps

  const resolve = useMutation<unknown, ApiError, { decision: "approved" | "denied"; grantTagSlugs?: string[] }>({
    mutationFn: ({ decision, grantTagSlugs }) =>
      api(`/api/admin/requests/${request.id}/resolve`, { method: "POST", json: { decision, grantTagSlugs } }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["adminRequests"] })
      qc.invalidateQueries({ queryKey: ["adminUsers"] })
    },
  })

  const toggle = (slug: string) =>
    setSelected((cur) => (cur.includes(slug) ? cur.filter((s) => s !== slug) : [...cur, slug]))

  return (
    <div className="glass-card flex flex-col gap-3">
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="font-medium text-th-fg">{request.user_name || request.user_email}</div>
          <div className="text-xs text-th-fg/50">{request.user_email}</div>
        </div>
        <div className="text-xs text-th-fg/40">{formatRelative(request.created_at_ms)}</div>
      </div>
      {request.message && <div className="text-sm text-th-fg/75 whitespace-pre-wrap">{request.message}</div>}
      {requestedTerms.length > 0 && (
        <div>
          <div className="mb-1 text-xs text-th-fg/50">Requested</div>
          <div className="flex flex-wrap gap-1">
            {requestedTerms.map((t) => <TagChip key={t} slug={t} />)}
          </div>
        </div>
      )}
      {request.status === "pending" && (
        <>
          <div>
            <div className="mb-1 text-xs text-th-fg/50">Grant tags</div>
            <div className="flex flex-wrap gap-1">
              {allTags.map((t) => (
                <TagChip key={t.slug} slug={t.slug} active={selected.includes(t.slug)} onClick={() => toggle(t.slug)} />
              ))}
            </div>
          </div>
          {resolve.error && (
            <div className="rounded-lg border border-red-500/40 bg-red-500/10 px-3 py-2 text-xs text-red-400">
              {resolve.error.serverMessage ?? `Resolve failed (${resolve.error.status})`}
            </div>
          )}
          <div className="flex gap-2">
            <button
              className="btn-primary flex-1"
              disabled={resolve.isPending}
              onClick={() => resolve.mutate({ decision: "approved", grantTagSlugs: selected })}
            >
              <Check size={14} /> {resolve.isPending && resolve.variables?.decision === "approved" ? "Approving…" : "Approve & grant"}
            </button>
            <button
              className="btn-danger"
              disabled={resolve.isPending}
              onClick={() => resolve.mutate({ decision: "denied" })}
            >
              <X size={14} /> {resolve.isPending && resolve.variables?.decision === "denied" ? "Denying…" : "Deny"}
            </button>
          </div>
        </>
      )}
      {request.status !== "pending" && (
        <div className={clsx(
          "rounded-lg border px-3 py-1.5 text-xs",
          request.status === "approved" ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-400" : "border-red-500/40 bg-red-500/10 text-red-400"
        )}>
          {request.status} · {formatRelative(request.resolved_at_ms)}
        </div>
      )}
    </div>
  )
}
